import React, { useState, useEffect, useRef } from 'react';
import { useInView } from 'framer-motion';

/**
 * AnimatedCounter component
 * Animates all numeric values inside `value` from 0 to their target when scrolled into view.
 *
 * Examples:
 * - "500+" -> "0+" ... "500+"
 * - "4.8x" -> "0.0x" ... "4.8x"
 * - "₹50M+" -> "₹0M+" ... "₹50M+"
 * - "12-18%" -> "0-0%" ... "12-18%"
 * - "320% increase" -> "0% increase" ... "320% increase"
 */
const AnimatedCounter = ({
  value,
  duration = 1800,
  className = '',
  prefix = '',
  suffix = '',
  component: Component = 'span'
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  // Compute zero placeholder string for instant initial render before view
  const getInitialZeroString = (val) => {
    const str = String(val ?? '');
    return str.replace(/([\d,]+(?:\.\d+)?)/g, (match) => {
      if (match.includes('.')) {
        const decimals = match.split('.')[1].length;
        return (0).toFixed(decimals);
      }
      return '0';
    });
  };

  const [displayValue, setDisplayValue] = useState(() => getInitialZeroString(value));

  useEffect(() => {
    if (!isInView) return;

    const rawStr = String(value ?? '');
    const tokens = [];
    const regex = /([\d,]+(?:\.\d+)?)/g;
    let match;

    while ((match = regex.exec(rawStr)) !== null) {
      const numStr = match[1].replace(/,/g, '');
      const numVal = parseFloat(numStr);
      if (!isNaN(numVal)) {
        const hasDecimals = match[1].includes('.');
        const decimalPlaces = hasDecimals ? match[1].split('.')[1].length : 0;
        const hasCommas = match[1].includes(',');
        tokens.push({
          fullMatch: match[0],
          index: match.index,
          length: match[0].length,
          target: numVal,
          decimals: decimalPlaces,
          hasCommas
        });
      }
    }

    if (tokens.length === 0) {
      setDisplayValue(rawStr);
      return;
    }

    let startTime = null;
    let animationFrameId;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Smooth cubic ease-out
      const ease = 1 - Math.pow(1 - progress, 3);

      let lastIdx = 0;
      let result = '';

      tokens.forEach((token) => {
        result += rawStr.slice(lastIdx, token.index);
        const currentVal = token.target * ease;
        let formattedVal;
        if (token.decimals > 0) {
          formattedVal = currentVal.toFixed(token.decimals);
        } else {
          formattedVal = Math.floor(currentVal).toString();
        }
        if (token.hasCommas) {
          const parts = formattedVal.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          formattedVal = parts.join('.');
        }
        result += formattedVal;
        lastIdx = token.index + token.length;
      });
      result += rawStr.slice(lastIdx);

      setDisplayValue(result);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        setDisplayValue(rawStr);
      }
    };

    animationFrameId = requestAnimationFrame(step);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isInView, value, duration]);

  return (
    <Component ref={ref} className={className}>
      {prefix}{displayValue}{suffix}
    </Component>
  );
};

export default AnimatedCounter;
